// Metric definitions editor — admin maintains the declarative catalogue
// of measurable product / test metrics without redeploying.
//
// A metric is *data*, not code: key + human label + value_type + bounds.
// The passport layer reads this catalogue to render known values with
// labels, units and bounds. Generic validation of measured values is not
// implemented here; scope splits product-level metrics from test protocols.
//
// Backend contract: GET/POST /admin/metrics, PATCH/DELETE
// /admin/metrics/:id. This admin page requires cycle.manage; API list/create
// additionally permit manufacturing_order.read/create as defined by the route.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { RequirePermission } from "@/components/RequirePermission";
import { ErrorState, LoadingState } from "@/components/States";
import { ApiError, adminMetricsApi } from "@/lib/api";
import type { MetricDefinition, MetricScope, MetricValueType } from "@/lib/types";
import { MetricScope as METRIC_SCOPES, MetricValueType as METRIC_VALUE_TYPES } from "@/lib/types";

const INPUT = "w-full px-3 py-2 rounded-md bg-surface border border-border text-sm";
const INPUT_MONO = `${INPUT} font-mono`;

// Map a mutation error to a Russian, user-facing string. 409 on create
// means the (scope, key) pair already exists.
function mutationErrorText(err: unknown, conflictMsg?: string): string {
  if (err instanceof ApiError) {
    if (err.status === 409 && conflictMsg) return conflictMsg;
    return err.message;
  }
  return err instanceof Error ? err.message : String(err);
}

export default function MetricsEditor() {
  return (
    <RequirePermission codes="cycle.manage">
      <Inner />
    </RequirePermission>
  );
}

function Inner() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [scope, setScope] = useState<MetricScope>("product");

  const metrics = useQuery({
    queryKey: ["metrics", scope],
    queryFn: () => adminMetricsApi.list(scope),
    staleTime: 30_000,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["metrics"] });

  return (
    <>
      <PageHeader
        eyebrow={t("eyebrows.adminMetrics")}
        title={t("metrics.title")}
        description={t("metrics.description")}
      />

      <div className="card p-4 mt-4 flex items-center gap-3">
        <label className="text-xs font-mono uppercase tracking-wider text-text-muted">
          scope
        </label>
        <select
          className={`${INPUT_MONO} w-auto`}
          value={scope}
          onChange={(e) => setScope(e.target.value as MetricScope)}
        >
          {METRIC_SCOPES.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
        <span className="text-text-muted text-xs">
          {scope === "product"
            ? t("metrics.scopeProduct")
            : t("metrics.scopeTest")}
        </span>
      </div>

      {metrics.isLoading && <LoadingState />}
      {metrics.isError && <ErrorState error={metrics.error} />}

      {metrics.data && (
        <div className="space-y-6 mt-4">
          <MetricsTable
            metrics={metrics.data.items}
            onChanged={invalidate}
          />
          <CreateMetricCard
            scope={scope}
            onCreated={invalidate}
          />
        </div>
      )}
    </>
  );
}

// ---------- table (list + inline edit + delete) ---------------------------

function MetricsTable({
  metrics,
  onChanged,
}: {
  metrics: MetricDefinition[];
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  return (
    <section className="card p-4 space-y-3">
      <h3 className="text-base">{t("metrics.tableTitle")}</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-text-muted text-xs">
            <tr>
              <th className="text-left px-2 py-1">key</th>
              <th className="text-left px-2 py-1">{t("metrics.colNameRu")}</th>
              <th className="text-left px-2 py-1">unit</th>
              <th className="text-left px-2 py-1">value_type</th>
              <th className="text-left px-2 py-1">min / max</th>
              <th className="text-left px-2 py-1">sort</th>
              <th className="text-left px-2 py-1">{t("metrics.colEnabled")}</th>
              <th className="w-32"></th>
            </tr>
          </thead>
          <tbody>
            {metrics.map((m) => (
              <MetricRow key={m.id} metric={m} onChanged={onChanged} />
            ))}
            {metrics.length === 0 && (
              <tr>
                <td
                  colSpan={8}
                  className="px-2 py-3 text-center text-text-muted text-xs"
                >
                  {t("metrics.empty")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function MetricRow({
  metric,
  onChanged,
}: {
  metric: MetricDefinition;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [nameRu, setNameRu] = useState(metric.name_ru);
  const [unit, setUnit] = useState(metric.unit ?? "");
  const [sortOrder, setSortOrder] = useState(String(metric.sort_order));
  const [enabled, setEnabled] = useState(metric.enabled);

  const update = useMutation({
    mutationFn: () =>
      adminMetricsApi.update(metric.id, {
        name_ru: nameRu.trim(),
        unit: unit.trim() || null,
        sort_order: Number(sortOrder) || 0,
        enabled,
      }),
    onSuccess: onChanged,
  });

  const del = useMutation({
    mutationFn: () => adminMetricsApi.remove(metric.id),
    onSuccess: onChanged,
  });

  const dirty =
    nameRu.trim() !== metric.name_ru ||
    (unit.trim() || "") !== (metric.unit ?? "") ||
    (Number(sortOrder) || 0) !== metric.sort_order ||
    enabled !== metric.enabled;

  return (
    <>
      <tr className="border-t border-border">
        <td className="px-2 py-1 font-mono text-xs align-middle">{metric.key}</td>
        <td className="px-2 py-1">
          <input
            className={INPUT}
            value={nameRu}
            onChange={(e) => setNameRu(e.target.value)}
          />
        </td>
        <td className="px-2 py-1">
          <input
            className={`${INPUT_MONO} w-24`}
            value={unit}
            placeholder="—"
            onChange={(e) => setUnit(e.target.value)}
          />
        </td>
        <td className="px-2 py-1 font-mono text-xs align-middle">
          {metric.value_type}
        </td>
        <td className="px-2 py-1 font-mono text-xs align-middle">
          {(metric.min_value ?? "—") + " / " + (metric.max_value ?? "—")}
        </td>
        <td className="px-2 py-1">
          <input
            type="number"
            className={`${INPUT_MONO} w-20`}
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
          />
        </td>
        <td className="px-2 py-1 text-center align-middle">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
        </td>
        <td className="px-2 py-1 text-right whitespace-nowrap">
          <button
            className="btn btn-primary text-xs mr-2"
            disabled={!dirty || update.isPending}
            onClick={() => update.mutate()}
          >
            {update.isPending ? "…" : t("common.save")}
          </button>
          <button
            className="text-text-muted hover:text-danger text-xs"
            disabled={del.isPending}
            onClick={() => {
              if (
                window.confirm(
                  t("metrics.confirmDelete", { name: metric.name_ru, key: metric.key }),
                )
              ) {
                del.mutate();
              }
            }}
            title={t("metrics.deleteTitle")}
          >
            {del.isPending ? t("metrics.deleting") : t("metrics.deleteAction")}
          </button>
        </td>
      </tr>
      {(update.isError || del.isError) && (
        <tr>
          <td colSpan={8} className="px-2 pb-2">
            {update.isError && (
              <ErrorBanner onDismiss={() => update.reset()}>
                {mutationErrorText(update.error)}
              </ErrorBanner>
            )}
            {del.isError && (
              <ErrorBanner onDismiss={() => del.reset()}>
                {mutationErrorText(
                  del.error,
                  t("metrics.deleteConflict"),
                )}
              </ErrorBanner>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// ---------- create form ---------------------------------------------------

function CreateMetricCard({
  scope,
  onCreated,
}: {
  scope: MetricScope;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [key, setKey] = useState("");
  const [nameRu, setNameRu] = useState("");
  const [nameEn, setNameEn] = useState("");
  const [unit, setUnit] = useState("");
  const [valueType, setValueType] = useState<MetricValueType>("number");
  const [minValue, setMinValue] = useState("");
  const [maxValue, setMaxValue] = useState("");
  const [sortOrder, setSortOrder] = useState("");

  const create = useMutation({
    mutationFn: () => {
      const min = minValue.trim() === "" ? null : Number(minValue);
      const max = maxValue.trim() === "" ? null : Number(maxValue);
      return adminMetricsApi.create({
        key: key.trim(),
        name_ru: nameRu.trim(),
        name_en: nameEn.trim() || null,
        unit: unit.trim() || null,
        value_type: valueType,
        min_value: Number.isFinite(min as number) ? min : null,
        max_value: Number.isFinite(max as number) ? max : null,
        sort_order: sortOrder.trim() === "" ? 0 : Number(sortOrder) || 0,
        scope,
      });
    },
    onSuccess: () => {
      setKey("");
      setNameRu("");
      setNameEn("");
      setUnit("");
      setValueType("number");
      setMinValue("");
      setMaxValue("");
      setSortOrder("");
      onCreated();
    },
  });

  const canCreate = key.trim().length > 0 && nameRu.trim().length > 0;
  // min/max only make sense for numeric metrics.
  const showBounds = valueType === "number";

  return (
    <section className="card p-4 space-y-3">
      <h3 className="text-base">{t("metrics.newMetric", { scope })}</h3>
      <p className="text-text-muted text-xs">
        {t("metrics.newMetricHint")}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <Field label="key">
          <input
            className={INPUT_MONO}
            value={key}
            placeholder={t("metrics.phKey")}
            onChange={(e) => setKey(e.target.value)}
          />
        </Field>
        <Field label={t("metrics.fNameRu")}>
          <input
            className={INPUT}
            value={nameRu}
            placeholder={t("metrics.phNameRu")}
            onChange={(e) => setNameRu(e.target.value)}
          />
        </Field>
        <Field label={t("metrics.fNameEn")}>
          <input
            className={INPUT}
            value={nameEn}
            placeholder={t("metrics.phNameEn")}
            onChange={(e) => setNameEn(e.target.value)}
          />
        </Field>
        <Field label={t("metrics.fUnit")}>
          <input
            className={INPUT_MONO}
            value={unit}
            placeholder={t("metrics.phUnit")}
            onChange={(e) => setUnit(e.target.value)}
          />
        </Field>
        <Field label="value_type">
          <select
            className={INPUT_MONO}
            value={valueType}
            onChange={(e) => setValueType(e.target.value as MetricValueType)}
          >
            {METRIC_VALUE_TYPES.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("metrics.fSortOrder")}>
          <input
            type="number"
            className={INPUT_MONO}
            value={sortOrder}
            placeholder="0"
            onChange={(e) => setSortOrder(e.target.value)}
          />
        </Field>
        {showBounds && (
          <>
            <Field label={t("metrics.fMinValue")}>
              <input
                type="number"
                className={INPUT_MONO}
                value={minValue}
                placeholder="—"
                onChange={(e) => setMinValue(e.target.value)}
              />
            </Field>
            <Field label={t("metrics.fMaxValue")}>
              <input
                type="number"
                className={INPUT_MONO}
                value={maxValue}
                placeholder="—"
                onChange={(e) => setMaxValue(e.target.value)}
              />
            </Field>
          </>
        )}
      </div>

      {create.isError && (
        <ErrorBanner onDismiss={() => create.reset()}>
          {mutationErrorText(
            create.error,
            t("metrics.createConflict"),
          )}
        </ErrorBanner>
      )}
      <button
        className="btn btn-primary"
        disabled={!canCreate || create.isPending}
        onClick={() => create.mutate()}
      >
        {create.isPending ? t("common.creating") : t("metrics.addMetric")}
      </button>
    </section>
  );
}

// ---------- atoms ---------------------------------------------------------

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-xs font-mono uppercase tracking-wider text-text-muted">
        {label}
      </span>
      {hint && <span className="block text-text-muted text-xs italic mt-0.5">{hint}</span>}
      <div className="mt-1">{children}</div>
    </label>
  );
}
